import { useStore } from '@nanostores/react'
import { DialogTitle } from '@radix-ui/react-dialog'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/shallow'

import { Dialog, DialogContent, DialogHeader } from '@/components/atoms/Dialog'
import ConfigPreview from '@/components/molecules/ConfigPreview'
import DealerForm from '@/components/molecules/DealerForm'
import EmailForm from '@/components/molecules/EmailForm'
import { useScopedI18n } from '@/locales/client'
import { $hasDealersAvailable } from '@/stores/Dealers'
import type { DialogTypes } from '@/stores/DialogsStore'
import useDialogsStore from '@/stores/DialogsStore'
import { useMobileLayoutStore } from '@/stores/MobileLayoutStore'
import { cn } from '@/util/cn'

const DIALOG_ID: DialogTypes = 'configurationPreview'

type TabType = 'dealer' | 'myself'

function ConfigPreviewDialog() {
  const t = useScopedI18n('dialogs.preview')
  const [activeTab, setActiveTab] = useState<TabType>('dealer')
  const { open } = useDialogsStore(
    useShallow(state => state.dialogs.configurationPreview),
  )
  const toggle = useDialogsStore(useShallow(state => state.toggle))
  const hubId = useDialogsStore(useShallow(store => store.dialogs.configurationPreview.hubId))
  const isMobile = useMobileLayoutStore(store => store.isMobile)
  const hasDealersAvailable = useStore($hasDealersAvailable)

  // Determine if dealer tab should be shown
  // Hide when dealers have been fetched and there are none
  const showDealerTab = hasDealersAvailable !== false
  
  // Set default tab to dealer when dealers are available or unknown (e.g. waiting for state selection)
  useEffect(() => {
    if (hasDealersAvailable === false) {
      setActiveTab('myself')
    } else {
      setActiveTab('dealer')
    }
  }, [hasDealersAvailable])

  return (
    <Dialog open={open} onOpenChange={() => toggle(DIALOG_ID)}>
      <DialogContent className="inset-[initial] inset-y-0 left-0 flex max-w-3xl translate-x-0 translate-y-0 flex-col content-between items-center gap-4 p-8 text-left font-sans transition-all duration-300 ease-in-out md:inset-[initial] lg:gap-8">
        <DialogHeader>
          <DialogTitle className="mt-4 text-center font-inter text-[32px] font-bold leading-tight text-rsf-black">
            {t('title')}
          </DialogTitle>
        </DialogHeader>

        {hubId && (
          <div
            id={`config-preview-big-${hubId}`}
          >
            <ConfigPreview
              hubId={hubId}
              className={cn('config-grid mb-2 h-3/5 md:h-auto', isMobile && 'config-grid-mobile')}
              isModal
            />
          </div>
        )}

        {/* Tab Switcher - only show when dealers are available */}
        {showDealerTab && (
          <div className="flex gap-6 border-b border-rsf-grey">
            <button
              type="button"
              onClick={() => setActiveTab('dealer')}
              className={cn(
                'relative pb-2 text-base font-semibold transition-colors',
                activeTab === 'dealer'
                  ? 'text-rsf-accent'
                  : 'text-rsf-grey hover:text-rsf-grey-dark'
              )}
            >
              {t('tabDealer')}
              <div
                className={cn(
                  'absolute bottom-0 left-0 right-0 h-[3px] rounded-full transition-colors mb-[-1px]',
                  activeTab === 'dealer' ? 'bg-rsf-accent' : 'bg-transparent'
                )}
              />
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('myself')}
              className={cn(
                'relative pb-2 text-base font-semibold transition-colors',
                activeTab === 'myself'
                  ? 'text-rsf-accent'
                  : 'text-rsf-grey hover:text-rsf-grey-dark'
              )}
            >
              {t('tabMyself')}
              <div
                className={cn(
                  'absolute bottom-0 left-0 right-0 h-[3px] rounded-full transition-colors mb-[-1px] ',
                  activeTab === 'myself' ? 'bg-rsf-accent' : 'bg-transparent'
                )}
              />
            </button>
          </div>
        )}

        {/* Show email form when: no dealers available OR "myself" tab is active */}
        {(!showDealerTab || activeTab === 'myself') ? (
          <EmailForm
            titleText={t('formTitle')}
            buttonText={t('formButton')}
            isSingleHub
            hubId={hubId!}
          />
        ) : (
          <DealerForm
            titleText={t('dealerFormTitle')}
            buttonText={t('tabDealer')}
            preselectedHubId={hubId ?? undefined}
            onClose={() => toggle(DIALOG_ID)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ConfigPreviewDialog
